<template>
    <section class="bg-background-light py-20 lg:py-28" :id="id">
        <div class="mx-auto max-w-[1200px] px-6 lg:px-10">
            <div class="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
                <div>
                    <h2 class="max-w-[560px] text-4xl font-black leading-[1.05] tracking-[-0.02em] text-text-main sm:text-5xl">
                        {{ t('projects.title') }}
                    </h2>
                    <p class="mt-4 max-w-[520px] text-lg text-text-muted">{{ t('projects.subtitle') }}</p>
                </div>

                <RouterLink
                    :to="getRoute('/projects')"
                    class="hidden shrink-0 items-center gap-2 rounded-xl border border-text-main/15 bg-white px-5 py-3 text-sm font-bold text-text-main transition-colors hover:border-text-main/40 sm:inline-flex"
                >
                    {{ t('projects.view_all') }}
                    <span class="material-symbols-outlined text-[18px]">arrow_forward</span>
                </RouterLink>
            </div>

            <div class="mt-12 grid grid-cols-1 gap-7 sm:grid-cols-2 lg:grid-cols-3">
                <RouterLink
                    v-for="project in featuredProjects"
                    :key="project.slug"
                    :to="{ name: 'project-details', params: { slug: project.slug, locale } }"
                    class="group flex flex-col overflow-hidden rounded-3xl border border-text-main/10 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_30px_70px_-30px_rgba(24,22,16,0.4)]"
                >
                    <!-- browser-framed app screenshot -->
                    <div class="border-b border-black/5 bg-[#faf8f5] px-4 py-2.5">
                        <div class="flex items-center gap-1.5">
                            <span class="h-2 w-2 rounded-full bg-[#e6ddd0]"></span>
                            <span class="h-2 w-2 rounded-full bg-[#e6ddd0]"></span>
                            <span class="h-2 w-2 rounded-full bg-[#e6ddd0]"></span>
                        </div>
                    </div>
                    <div class="relative aspect-[16/11] overflow-hidden bg-gray-100">
                        <img
                            :src="shotFor(project)"
                            :alt="projectContent(project).title"
                            class="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
                            loading="lazy"
                            decoding="async"
                        />
                    </div>

                    <div class="flex items-start justify-between gap-3 p-6">
                        <div class="min-w-0">
                            <span class="text-xs font-bold uppercase tracking-wide text-primary">{{ projectContent(project).tag }}</span>
                            <h3 class="mt-1 text-lg font-bold text-text-main transition-colors group-hover:text-primary">
                                {{ projectContent(project).title }}
                            </h3>
                            <!-- One-line outcome; omitted when the project has none yet. -->
                            <p v-if="resultFor(project)" class="mt-1.5 flex items-start gap-1.5 text-sm leading-snug text-text-muted">
                                <span class="material-symbols-outlined mt-px text-[16px] text-primary">trending_up</span>
                                <span>{{ resultFor(project) }}</span>
                            </p>
                        </div>
                        <span class="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-background-light text-text-main transition-all group-hover:bg-primary">
                            <span class="material-symbols-outlined text-[18px]">arrow_outward</span>
                        </span>
                    </div>
                </RouterLink>
            </div>

            <div class="mt-10 flex justify-center sm:hidden">
                <RouterLink
                    :to="getRoute('/projects')"
                    class="inline-flex items-center gap-2 rounded-xl border border-text-main/15 bg-white px-6 py-3 text-sm font-bold text-text-main"
                >
                    {{ t('projects.view_all') }}
                    <span class="material-symbols-outlined text-[18px]">arrow_forward</span>
                </RouterLink>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { RouterLink } from 'vue-router';

import { projects, type ProjectItem, type ProjectLocale } from '../../data/projects';

defineProps<{ id?: string }>();

const { t, locale } = useI18n();

const activeLocale = computed<ProjectLocale>(() => (locale.value === 'nl' ? 'nl' : 'en'));

const featuredSlugs = ['food-la-route-platform', 'broednest-platform', 'highmark-software-website'];

// Prefer the wide desktop captures for the showcase; fall back to the card image.
const desktopShots: Record<string, string> = {
    'food-la-route-platform': '/images/deskop_foodlaroute.webp',
    'broednest-platform': '/images/desktop_broednest.webp',
    'highmark-software-website': '/images/desktop_highmark.webp',
    'sman-cars-platform': '/images/desktop_smancars.webp',
    'songup-platform': '/images/desktop_songup.webp',
    'suporta-platform': '/images/desktop_suporta.webp',
    'tribeo-marketplace': '/images/desktop_tribeo.webp',
};

const shotFor = (project: ProjectItem) => desktopShots[project.slug] ?? project.image;

const featuredProjects = computed<ProjectItem[]>(() =>
    featuredSlugs
        .map((slug) => projects.find((p) => p.slug === slug))
        .filter((p): p is ProjectItem => Boolean(p))
);

const projectContent = (project: ProjectItem) => project[activeLocale.value] ?? project.en;

/**
 * Result lines that are still [PLACEHOLDER: ...] show on the dev server only, so
 * an unfilled project never ships a bracketed note to visitors.
 */
const resultFor = (project: ProjectItem) => {
    const result = projectContent(project).result;
    if (!result) return undefined;
    if (!import.meta.env.DEV && result.startsWith('[PLACEHOLDER')) return undefined;
    return result;
};

const getRoute = (path: string) => {
    const currentLocale = locale.value;
    if (path.startsWith('/')) return `/${currentLocale}${path}`.replace('//', '/');
    return path;
};
</script>
